import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatApiDetail, getMarketingBajaTokenStatus, postMarketingBaja } from '../lib/api';

/**
 * Baja de promociones por WhatsApp (doc. 05 §9): /baja (formulario con teléfono) o /baja?t=… (enlace firmado).
 * Los avisos del servicio de los pedidos no se afectan. También se puede responder BAJA por WhatsApp.
 */
export default function BajaPage() {
  const [params] = useSearchParams();
  const token = (params.get('t') || '').trim();
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState(token ? 'checking' : 'form');
  const [masked, setMasked] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Baja de promociones';
  }, []);

  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    getMarketingBajaTokenStatus(token)
      .then((data) => {
        if (cancelled) return;
        if (!data || !data.valid) {
          setStatus('invalid');
          setMessage('Este enlace de baja no es válido o ya venció. Puede usar el formulario con su teléfono.');
          return;
        }
        setMasked(data.phone_masked || '');
        if (data.already_opted_out) {
          setStatus('done');
          setMessage('Este número ya estaba dado de baja de promociones.');
          return;
        }
        setStatus('token-ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('invalid');
        setMessage('No se pudo verificar el enlace. Intente más tarde o use el formulario.');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (body) => {
    setSubmitting(true);
    setMessage('');
    try {
      const data = await postMarketingBaja(body);
      setMasked(data.phone_masked || masked);
      setMessage(
        data.message ||
          `Listo. El número terminación ${data.phone_last4 || '****'} ya no recibirá promociones.`,
      );
      setStatus('done');
    } catch (e) {
      setMessage(formatApiDetail(e?.response?.data?.detail, 'No se pudo registrar la baja. Intente de nuevo.'));
      setStatus(token ? 'token-ready' : 'form');
    } finally {
      setSubmitting(false);
    }
  };

  const card = 'mt-6 rounded-[22px] border border-[#F1DFC8] bg-[#FFFDF8] p-5';

  return (
    <div className="py-10 md:py-14" data-testid="baja-page">
      <div className="mx-auto max-w-lg px-4">
        <h1 className="font-heading text-3xl font-extrabold text-[#2B1A12]">Baja de promociones</h1>
        <p className="mt-2 text-[#6B5444] leading-relaxed">
          Escriba el número de WhatsApp con el que pidió y pulse «Dar de baja». Dejaremos de enviarle promociones;
          seguirá recibiendo solo los avisos de sus pedidos. También puede responder <strong>BAJA</strong> a cualquier
          mensaje nuestro de WhatsApp.
        </p>

        {status === 'checking' ? (
          <p className="mt-6 text-sm text-[#6B5444]" role="status">
            Verificando enlace…
          </p>
        ) : null}

        {status === 'invalid' ? (
          <p className="mt-6 text-sm text-[#7A3E00] bg-[#FFF6EA] border border-[#F2B33D] rounded-2xl p-4" role="alert">
            {message}
          </p>
        ) : null}

        {status === 'token-ready' ? (
          <div className={card}>
            <p className="text-sm">
              Teléfono: <strong>{masked || '***'}</strong>
            </p>
            <button
              type="button"
              className="t-btn t-btn-primary w-full mt-4 min-h-[48px]"
              disabled={submitting}
              onClick={() => submit({ token })}
              data-testid="baja-confirm-token"
            >
              {submitting ? 'Registrando…' : 'Dar de baja'}
            </button>
            {message ? <p className="mt-3 text-sm text-[#B8321F]">{message}</p> : null}
          </div>
        ) : null}

        {status === 'form' || status === 'invalid' ? (
          <form
            className={card}
            onSubmit={(e) => {
              e.preventDefault();
              submit({ phone });
            }}
            data-testid="baja-phone-form"
          >
            <label htmlFor="baja-phone" className="block font-semibold">
              Teléfono (WhatsApp)
            </label>
            <input
              id="baja-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="mt-1.5 h-12 w-full rounded-xl border border-[#F1DFC8] bg-white px-3"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
              placeholder="10 dígitos"
              data-testid="baja-phone-input"
            />
            <button type="submit" className="t-btn t-btn-primary w-full mt-4 min-h-[48px]" disabled={submitting} data-testid="baja-submit">
              {submitting ? 'Registrando…' : 'Dar de baja'}
            </button>
            {message && status === 'form' ? (
              <p className="mt-3 text-sm text-[#B8321F]" role="alert">
                {message}
              </p>
            ) : null}
          </form>
        ) : null}

        {status === 'done' ? (
          <div className={card} role="status" data-testid="baja-done">
            <p className="font-bold">Baja registrada</p>
            <p className="mt-2 text-[#6B5444]">{message}</p>
          </div>
        ) : null}

        <p className="mt-8 text-sm">
          <Link to="/aviso-de-privacidad" className="underline text-[#C2410C]">
            Aviso de privacidad
          </Link>
          {' · '}
          <Link to="/" className="underline text-[#C2410C]">
            Inicio
          </Link>
        </p>
      </div>
    </div>
  );
}
